import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { MapPinned, Plus, Check, X } from 'lucide-react';

// Managed Service Footprint markets. Global Administrator only: add, rename,
// activate/retire. Non-owners see a read-only list.
const MarketManager = () => {
  const { token, user } = useContext(AuthContext);
  const isOwner = user?.role === 'owner';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [markets, setMarkets] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/markets?include_inactive=true`, authHeaders);
    setMarkets(r.data || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/admin/markets`, { name: newName.trim() }, authHeaders);
      setNewName('');
      toast.success('Market added');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add market');
    }
  };

  const saveRename = async (id) => {
    try {
      await axios.put(`${API}/admin/markets/${id}`, { name: editName.trim() }, authHeaders);
      setEditId(null);
      toast.success('Market renamed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rename');
    }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API}/admin/markets/${m.id}`, { active: !m.active }, authHeaders);
      toast.success(m.active ? 'Market retired' : 'Market reactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
  };

  return (
    <div data-testid="market-manager">
      {isOwner && (
        <div className="flex items-center gap-2 mb-4 max-w-md">
          <Input placeholder="New market name…" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            data-testid="market-new-input" />
          <button onClick={add} data-testid="market-add-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] whitespace-nowrap">
            <Plus size={15} /> Add
          </button>
        </div>
      )}
      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {markets.map((m) => (
            <div key={m.id} data-testid={`market-row-${m.id}`}
              className="flex items-center gap-3 px-4 py-3">
              <MapPinned size={16} className="text-[#8FC93A] shrink-0" />
              {editId === m.id ? (
                <>
                  <Input className="max-w-xs" value={editName}
                    onChange={(e) => setEditName(e.target.value)} data-testid="market-edit-input" />
                  <button onClick={() => saveRename(m.id)} className="text-green-600" data-testid="market-save-btn"><Check size={18} /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400"><X size={18} /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm font-medium ${m.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                    {m.name}
                  </span>
                  {!m.active && <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">Retired</span>}
                  {isOwner && (
                    <>
                      <button onClick={() => { setEditId(m.id); setEditName(m.name); }}
                        className="text-xs font-semibold text-[#0E2A47] hover:underline" data-testid={`market-rename-${m.id}`}>
                        Rename
                      </button>
                      <div className="flex items-center gap-1.5">
                        <Switch checked={m.active} onCheckedChange={() => toggleActive(m)}
                          data-testid={`market-toggle-${m.id}`} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {markets.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">No markets defined.</div>}
        </CardContent>
      </Card>
      {!isOwner && (
        <p className="mt-3 text-xs text-slate-400">Only a Global Administrator can add, rename or retire markets.</p>
      )}
    </div>
  );
};

export default MarketManager;
